import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Wrench, ChevronRight, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState } from "@/components/EmptyState";
import { MachineDialog } from "@/components/MachineDialog";
import { PageHeader } from "@/components/PageHeader";
import { formatDate, num, daysText } from "@/lib/format";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const STATUS_FILTERS = [
  { value: "all", label: "Alle" },
  { value: "overdue", label: "Forfalt" },
  { value: "upcoming", label: "Nærmar seg" },
  { value: "ok", label: "OK" },
];

export default function Maskiner() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const customerFilter = params.get("customer");
  const [machines, setMachines] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [dialogOpen, setDialogOpen] = useState(params.get("ny") === "1");

  const load = () => {
    setLoading(true);
    const url = customerFilter ? `/machines?customer_id=${customerFilter}` : "/machines";
    api.get(url).then((r) => setMachines(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (customerFilter) api.get(`/customers/${customerFilter}`).then((r) => setCustomerName(r.data.name)).catch(() => setCustomerName(""));
    else setCustomerName("");
  };
  useEffect(load, [customerFilter]);

  useEffect(() => {
    if (params.get("ny") === "1") {
      setDialogOpen(true);
      const p = new URLSearchParams(params);
      p.delete("ny");
      setParams(p, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    return machines.filter((m) => {
      if (status !== "all" && m.status?.overall_status !== status) return false;
      if (!term) return true;
      return [m.label, m.customer_name, m.serial_number, m.internal_number, m.brand, m.type]
        .filter(Boolean).join(" ").toLowerCase().includes(term);
    });
  }, [machines, q, status]);

  const counts = useMemo(() => {
    const c = { all: machines.length, overdue: 0, upcoming: 0, ok: 0 };
    machines.forEach((m) => { const s = m.status?.overall_status; if (c[s] !== undefined) c[s] += 1; });
    return c;
  }, [machines]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Maskiner"
        description={customerFilter && customerName ? (
          <span className="inline-flex items-center gap-2">
            Viser maskiner for <strong className="text-foreground">{customerName}</strong>
            <button type="button" onClick={() => navigate("/maskiner")} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs hover:bg-accent" data-testid="clear-customer-filter"><X className="h-3 w-3" /> Vis alle</button>
          </span>
        ) : "Heile maskinparken med fristar for service og årskontroll."}
        actions={<Button onClick={() => setDialogOpen(true)} data-testid="new-machine-button"><Plus /> Ny maskin</Button>}
      />

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="machine-search-input" placeholder="Søk maskin, kunde, serienr, maskinnr …" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-card pl-9" />
        </div>
        <ToggleGroup type="single" value={status} onValueChange={(v) => v && setStatus(v)} className="no-scrollbar justify-start gap-1.5 overflow-x-auto" data-testid="machine-status-filter">
          {STATUS_FILTERS.map((f) => (
            <ToggleGroupItem key={f.value} value={f.value} data-testid={`machine-filter-${f.value}`} className="h-9 shrink-0 gap-1.5 rounded-full border px-3 text-xs data-[state=on]:border-transparent data-[state=on]:bg-[#0B0D10] data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-[#0B0D10]">
              {f.label} <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts[f.value]}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-sm">
          <EmptyState
            className="m-4 border-0"
            icon={Wrench}
            title={machines.length === 0 ? "Ingen maskiner" : "Ingen treff"}
            description={machines.length === 0 ? "Registrer den første maskina for å starte oppfølging av service og årskontroll." : "Prøv eit anna søk eller filter."}
            action={machines.length === 0 && <Button onClick={() => setDialogOpen(true)}><Plus /> Ny maskin</Button>}
          />
        </Card>
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="space-y-2 md:hidden" data-testid="machine-card-list">
            {filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/maskiner/${m.id}`)}
                data-testid={`machine-card-${m.id}`}
                className="pressable flex w-full items-center gap-3 rounded-lg border bg-card p-3 text-left shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold">{m.label}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{m.customer_name || "—"}{m.serial_number ? ` · SN ${m.serial_number}` : ""}</div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span>Service: <span className="font-medium text-foreground">{m.status?.next_service_date ? formatDate(m.status.next_service_date) : "—"}</span></span>
                    <span>Årskontroll: <span className="font-medium text-foreground">{m.status?.next_control_date ? formatDate(m.status.next_control_date) : "—"}</span></span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusPill status={m.status?.overall_status} />
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>

          {/* Desktop: table */}
          <Card className="hidden overflow-hidden shadow-sm md:block">
            <Table data-testid="machine-list-table">
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead>Maskin</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead className="text-right">Timetal</TableHead>
                  <TableHead>Neste service</TableHead>
                  <TableHead>Årskontroll</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((m) => (
                  <TableRow key={m.id} onClick={() => navigate(`/maskiner/${m.id}`)} className="row-hover cursor-pointer" data-testid={`machine-row-${m.id}`}>
                    <TableCell>
                      <div className="font-semibold">{m.label}</div>
                      {m.serial_number && <div className="text-xs text-muted-foreground">SN {m.serial_number}</div>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{m.customer_name || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{num(m.current_hours)}</TableCell>
                    <TableCell>
                      <div className="tabular-nums">{m.status?.next_service_date ? formatDate(m.status.next_service_date) : "—"}</div>
                      {m.status?.service_status && <div className="text-xs text-muted-foreground">{daysText(m.status.service_days_remaining)}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="tabular-nums">{m.status?.next_control_date ? formatDate(m.status.next_control_date) : "—"}</div>
                      {m.status?.control_status && <div className="text-xs text-muted-foreground">{daysText(m.status.control_days_remaining)}</div>}
                    </TableCell>
                    <TableCell><StatusPill status={m.status?.overall_status} /></TableCell>
                    <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <MachineDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultCustomerId={customerFilter} onSaved={load} />
    </div>
  );
}
